import React, { useState, useEffect, useRef } from 'react';
import { 
  probarObtenerTag, 
  probarObtenerIdentificadores, 
  probarExtraccionLocalizacion,
  probarBuscarServicio,
  probarCrearMarcaje, 
  probarCrearMarcajeTurno,
  ejecutarFlujoCompleto 
} from './ApiMarcajesTester';

const ApiTesterComponent = () => {
  const [codPersona, setCodPersona] = useState('11959');
  const [empresa, setEmpresa] = useState('0001');
  const [delegacion, setDelegacion] = useState('06');
  const [localizacion, setLocalizacion] = useState('06/13853/01');
  const [servicioInfo, setServicioInfo] = useState({ idReg: '', serviceTag: '', empresa: '' });
  const [tag, setTag] = useState('');
  const [resultados, setResultados] = useState([]);
  const [ejecutando, setEjecutando] = useState(false);
  
  // Para marcaje de turno
  const [fecha, setFecha] = useState(new Date().toISOString().split('T')[0]);
  const [horaEntrada, setHoraEntrada] = useState('08:00');
  const [horaSalida, setHoraSalida] = useState('16:00');
  
  // Limitar la cantidad de mensajes en resultados
  const MAX_LOGS = 100;
  const resultsContainerRef = useRef(null);
  
  // Referencias a las funciones originales para restaurarlas cuando se desmonte el componente
  const originalConsoleLog = useRef(console.log);
  const originalConsoleError = useRef(console.error);

  // Función para registrar mensajes en los resultados
  const log = (mensaje, tipo = 'info') => {
    setResultados(prev => {
      const newResults = [...prev, { mensaje, tipo, timestamp: new Date().toLocaleTimeString() }];
      // Limitar la cantidad de mensajes para evitar bloqueos
      return newResults.slice(-MAX_LOGS);
    });
  };

  // Sobrescribir console.log y console.error al montar el componente
  useEffect(() => {
    console.log = (...args) => {
      originalConsoleLog.current(...args);
      const mensaje = args.map(arg => 
        typeof arg === 'object' ? 
          arg instanceof Error ? 
            arg.message : 
            JSON.stringify(arg, null, 2).substring(0, 500) 
          : String(arg)
      ).join(' ');
      log(mensaje);
    };

    console.error = (...args) => {
      originalConsoleError.current(...args);
      const mensaje = args.map(arg => 
        typeof arg === 'object' ? 
          arg instanceof Error ? 
            arg.message : 
            JSON.stringify(arg, null, 2).substring(0, 500) 
          : String(arg)
      ).join(' ');
      log(mensaje, 'error');
    };

    // Restaurar las funciones originales al desmontar
    return () => {
      console.log = originalConsoleLog.current;
      console.error = originalConsoleError.current;
    };
  }, []);
  
  // Auto-scroll al final de los resultados
  useEffect(() => {
    if (resultsContainerRef.current) {
      resultsContainerRef.current.scrollTop = resultsContainerRef.current.scrollHeight;
    }
  }, [resultados]);

  // Funciones para ejecutar las pruebas
  const handleProbarObtenerTag = async () => {
    setEjecutando(true);
    setResultados([]);
    try {
      const resultado = await probarObtenerTag(codPersona);
      if (resultado) {
        setTag(resultado);
      }
    } catch (error) {
      log(`Error: ${error.message}`, 'error');
    } finally {
      setEjecutando(false);
    }
  };

  const handleProbarIdentificadores = async () => {
    setEjecutando(true);
    setResultados([]);
    try {
      const identificadores = await probarObtenerIdentificadores(empresa);
      if (identificadores?.length > 0) {
        log(`Se encontraron ${identificadores.length} identificadores`);
        log("Usa 'Probar buscar servicio' para encontrar el identificador específico");
      }
    } catch (error) {
      log(`Error: ${error.message}`, 'error');
    } finally {
      setEjecutando(false);
    }
  };
  
  const extraerDatosLocalizacion = () => {
    try {
      if (!localizacion) return null;
      
      const partes = localizacion.split('/');
      return {
        delegacion: partes[0],
        contrato: partes[1],
        servicio: partes.length > 2 ? partes[2] : "01"
      };
    } catch (error) {
      return null;
    }
  };
  
  const handleProbarExtraccionLocalizacion = () => {
    setResultados([]);
    try {
      const resultado = probarExtraccionLocalizacion(localizacion);
      return resultado;
    } catch (error) {
      log(`Error: ${error.message}`, 'error');
      return null;
    }
  };
  
  const handleProbarBuscarServicio = async () => {
    setEjecutando(true);
    setResultados([]);
    try {
      // Extraer primero los datos de localización
      const datosLocalizacion = handleProbarExtraccionLocalizacion();
      if (datosLocalizacion) {
        const servicioEncontrado = await probarBuscarServicio(
          empresa, 
          datosLocalizacion.delegacion, 
          datosLocalizacion.contrato, 
          datosLocalizacion.servicio
        );
        if (servicioEncontrado) {
          // Ahora servicioEncontrado incluye toda la información necesaria
          setServicioInfo(servicioEncontrado);
          log(`Identificador para URL (idReg): ${servicioEncontrado.idReg}`);
          log(`Identificador para Body (serviceTag): ${servicioEncontrado.serviceTag}`);
          log(`Empresa: ${servicioEncontrado.empresa}`);
        }
      }
    } catch (error) {
      log(`Error: ${error.message}`, 'error');
    } finally {
      setEjecutando(false);
    }
  };

  const handleProbarMarcaje = async () => {
    if (!servicioInfo.idReg || !servicioInfo.serviceTag || !tag) {
      log('Necesitas un identificador de servicio completo y un tag de persona para probar el marcaje', 'error');
      return;
    }

    setEjecutando(true);
    setResultados([]);
    try {
      await probarCrearMarcaje(servicioInfo, tag);
    } catch (error) {
      log(`Error: ${error.message}`, 'error');
    } finally {
      setEjecutando(false);
    }
  };
  
  const handleProbarMarcajeTurno = async () => {
    if (!servicioInfo.idReg || !servicioInfo.serviceTag || !tag) {
      log('Necesitas un identificador de servicio completo y un tag de persona para probar el marcaje de turno', 'error');
      return;
    }

    setEjecutando(true);
    setResultados([]);
    try {
      await probarCrearMarcajeTurno(
        servicioInfo, 
        tag, 
        fecha, 
        horaEntrada, 
        horaSalida
      );
    } catch (error) {
      log(`Error: ${error.message}`, 'error');
    } finally {
      setEjecutando(false);
    }
  };

  const handleFlujoCompleto = async () => {
    setEjecutando(true);
    setResultados([]);
    try {
      await ejecutarFlujoCompleto(codPersona, empresa, localizacion);
    } catch (error) {
      log(`Error: ${error.message}`, 'error');
    } finally {
      setEjecutando(false);
    }
  };

  const limpiarResultados = () => {
    setResultados([]);
  };

  const handleIdRegChange = (e) => {
    setServicioInfo(prev => ({ ...prev, idReg: e.target.value }));
  };

  const handleServiceTagChange = (e) => {
    setServicioInfo(prev => ({ ...prev, serviceTag: e.target.value }));
  };

  const handleEmpresaInfoChange = (e) => {
    setServicioInfo(prev => ({ ...prev, empresa: e.target.value }));
  };

  return (
    <div className="api-tester">
      <h2>Probador de API de Marcajes</h2>
      
      <div className="test-section">
        <h3>Parámetros de búsqueda</h3>
        <div className="form-group">
          <label>Código de Persona:</label>
          <input
            type="text"
            value={codPersona}
            onChange={(e) => setCodPersona(e.target.value)}
            disabled={ejecutando}
          />
        </div>
        
        <div className="form-group">
          <label>Código de Empresa:</label>
          <input
            type="text"
            value={empresa}
            onChange={(e) => setEmpresa(e.target.value)}
            disabled={ejecutando}
          />
        </div>
        
        <div className="form-group">
          <label>Localización:</label>
          <input
            type="text"
            value={localizacion}
            onChange={(e) => setLocalizacion(e.target.value)}
            disabled={ejecutando}
            placeholder="formato: delegacion/contrato/servicio (ej: 06/13853/01)"
          />
        </div>
        
        <div className="form-group">
          <label>Tag obtenido:</label>
          <input
            type="text"
            value={tag}
            onChange={(e) => setTag(e.target.value)}
            disabled={ejecutando}
          />
        </div>
        
        <div className="form-group-section">
          <h4>Información del Servicio</h4>
          <div className="form-group">
            <label>ID Reg (para URL):</label>
            <input
              type="text"
              value={servicioInfo.idReg}
              onChange={handleIdRegChange}
              disabled={ejecutando}
              placeholder="MDAwMV9fMDZfXzEzODUzX18wMQ=="
            />
          </div>
          
          <div className="form-group">
            <label>Service Tag (para Body):</label>
            <input
              type="text"
              value={servicioInfo.serviceTag}
              onChange={handleServiceTagChange}
              disabled={ejecutando}
              placeholder="049C1B5C196181"
            />
          </div>
          
          <div className="form-group">
            <label>Empresa:</label>
            <input
              type="text"
              value={servicioInfo.empresa}
              onChange={handleEmpresaInfoChange}
              disabled={ejecutando}
              placeholder="0001"
            />
          </div>
        </div>
        
        <div className="form-group-section">
          <h4>Datos para Marcaje de Turno</h4>
          <div className="form-group">
            <label>Fecha:</label>
            <input
              type="date"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
              disabled={ejecutando}
            />
          </div>
          
          <div className="form-group">
            <label>Hora de Entrada:</label>
            <input
              type="time"
              value={horaEntrada}
              onChange={(e) => setHoraEntrada(e.target.value)}
              disabled={ejecutando}
            />
          </div>
          
          <div className="form-group">
            <label>Hora de Salida:</label>
            <input
              type="time"
              value={horaSalida}
              onChange={(e) => setHoraSalida(e.target.value)}
              disabled={ejecutando}
            />
          </div>
        </div>
      </div>
      
      <div className="test-actions">
        <h3>Pruebas</h3>
        <div className="buttons">
          <button onClick={handleProbarObtenerTag} disabled={ejecutando}>
            Probar obtener tag
          </button>
          <button onClick={handleProbarIdentificadores} disabled={ejecutando}>
            Probar obtener identificadores
          </button>
          <button onClick={handleProbarExtraccionLocalizacion} disabled={ejecutando}>
            Probar extracción localización
          </button>
          <button onClick={handleProbarBuscarServicio} disabled={ejecutando}>
            Probar buscar servicio
          </button>
          <button onClick={handleProbarMarcaje} disabled={ejecutando || !servicioInfo.idReg || !servicioInfo.serviceTag || !tag}>
            Probar crear marcaje
          </button>
          <button onClick={handleProbarMarcajeTurno} disabled={ejecutando || !servicioInfo.idReg || !servicioInfo.serviceTag || !tag}>
            Probar marcaje de turno
          </button>
          <button onClick={handleFlujoCompleto} disabled={ejecutando}>
            Ejecutar flujo completo
          </button>
          <button onClick={limpiarResultados} className="clear-button">
            Limpiar resultados
          </button>
        </div>
      </div>
      
      <div className="results-section">
        <h3>Resultados {ejecutando && <span className="spinner"></span>}</h3>
        <div className="results-container" ref={resultsContainerRef}>
          {resultados.length === 0 ? (
            <p>Ejecuta una prueba para ver los resultados...</p>
          ) : (
            <div className="logs">
              {resultados.map((resultado, index) => (
                <div key={index} className={`log-entry ${resultado.tipo}`}>
                  <span className="timestamp">{resultado.timestamp}</span>
                  <span className="mensaje">{resultado.mensaje}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      
      <style jsx="true">{`
        .api-tester {
          font-family: Arial, sans-serif;
          max-width: 800px;
          margin: 20px auto;
          padding: 20px;
          border: 1px solid #ddd;
          border-radius: 8px;
          background-color: #f9f9f9;
        }
        
        h2, h3, h4 {
          color: #333;
        }
        
        h4 {
          margin-top: 20px;
          margin-bottom: 10px;
          border-bottom: 1px solid #ddd;
          padding-bottom: 5px;
        }
        
        .form-group-section {
          margin-top: 20px;
          padding: 10px;
          background-color: #f0f0f0;
          border-radius: 4px;
          border-left: 4px solid #4CAF50;
        }
        
        .form-group {
          margin-bottom: 15px;
          display: flex;
          align-items: center;
        }
        
        .form-group label {
          width: 180px;
          font-weight: bold;
        }
        
        .form-group input {
          flex: 1;
          padding: 8px;
          border: 1px solid #ccc;
          border-radius: 4px;
        }
        
        .buttons {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          margin-bottom: 20px;
        }
        
        button {
          padding: 10px 15px;
          background-color: #4CAF50;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-weight: bold;
        }
        
        button:hover {
          background-color: #45a049;
        }
        
        button:disabled {
          background-color: #cccccc;
          cursor: not-allowed;
        }
        
        .clear-button {
          background-color: #f44336;
        }
        
        .clear-button:hover {
          background-color: #d32f2f;
        }
        
        .results-container {
          background-color: #333;
          color: #fff;
          padding: 15px;
          border-radius: 4px;
          max-height: 400px;
          overflow-y: auto;
          font-family: monospace;
        }
        
        .log-entry {
          margin-bottom: 5px;
          padding: 5px 0;
          border-bottom: 1px solid #555;
        }
        
        .timestamp {
          color: #888;
          margin-right: 10px;
        }
        
        .error {
          color: #ff6b6b;
        }
        
        .info {
          color: #7ed6df;
        }
        
        .spinner {
          display: inline-block;
          width: 20px;
          height: 20px;
          border: 3px solid rgba(255,255,255,.3);
          border-radius: 50%;
          border-top-color: #fff;
          animation: spin 1s ease-in-out infinite;
          -webkit-animation: spin 1s ease-in-out infinite;
          margin-left: 10px;
          vertical-align: middle;
        }
        
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @-webkit-keyframes spin {
          to { -webkit-transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ApiTesterComponent;